import { AppsIcon } from '@daily/shared/components/Icons/AppsIcon';
import { BreakoutIcon } from '@daily/shared/components/Icons/BreakoutIcon';
import { ChatIcon } from '@daily/shared/components/Icons/ChatIcon';
import { EllipsisIcon } from '@daily/shared/components/Icons/EllipsisIcon';
import { GearIcon } from '@daily/shared/components/Icons/GearIcon';
import { HelpIcon } from '@daily/shared/components/Icons/HelpIcon';
import { RecordIcon } from '@daily/shared/components/Icons/RecordIcon';
import { UserIcon } from '@daily/shared/components/Icons/UserIcon';
import { Text } from '@daily/shared/components/Text';
import { useSnackbar } from '@daily/shared/contexts/Snackbar';
import { useTheme } from '@daily/shared/contexts/Theme';
import { useMediaQuery } from '@daily/shared/hooks/useMediaQuery';
import { useResize } from '@daily/shared/hooks/useResize';
import {
  useLocalSessionId,
  useParticipantProperty,
  useRecording as useDailyRecording,
  useWaitingParticipants,
} from '@daily-co/daily-react';
import classnames from 'classnames';
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { useBreakoutSessionState } from '/components/BreakoutRooms/hooks/useBreakoutSessionState';
import { SettingsModal } from '/components/SettingsModal';
import { AudioControls } from '/components/Tray/AudioControls';
import { ACTION_CUSTOM_BUTTON_CLICK } from '/components/Tray/constant';
import { CustomIcon } from '/components/Tray/CustomIcon';
import { RaiseHandControls } from '/components/Tray/RaiseHandControls';
import { RecordControls } from '/components/Tray/RecordControls';
import { TrayBadge } from '/components/Tray/TrayBadge';
import { TrayButton } from '/components/Tray/TrayButton';
import { VideoControls } from '/components/Tray/VideoControls';
import { useCallState } from '/contexts/CallProvider';
import { useIframeDriver } from '/contexts/IframeDriverProvider';
import { useOrderedParticipantIds } from '/contexts/ParticipantsProvider';
import { useRecording } from '/contexts/RecordingProvider';
import { useChatUnread } from '/features/chat/contexts/ChatProvider';
import { EmojiReactionControls } from '/features/emoji-reactions/components/EmojiReactionControls';
import { useCallConfig } from '/hooks/useCallConfig';
import { useCustomIntegrations } from '/hooks/useCustomIntegrations';
import { useHandRaisingBadgeStatus } from '/hooks/useHandRaisingBadgeStatus';
import { robotsClassName } from '/lib/robots';
import { useSidebarView } from '/lib/state/core';
import { useCustomTrayButtons } from '/lib/state/custom';
import { useMobileTray, useShowMobileMoreMenu } from '/lib/state/layout';
import { useAppsModal, useSettingsView } from '/lib/state/modals';
import { useRecordingUI, useStartRecordingModal } from '/lib/state/recordings';

import { MobileMoreMenu } from './MobileMoreMenu';

export const TRAY_MAX_WIDTH = 343;
const CONTENT_HEIGHT = 44;

export const MobileTray: React.FC<React.PropsWithChildren<unknown>> = memo(
  () => {
    const trayRef = useRef(null);
    const { colors, isDarkMode } = useTheme();
    const { t } = useTranslation();
    const { addMessage } = useSnackbar();
    const [showMobileTray] = useMobileTray();
    const [settingsView, setSettingsView] = useSettingsView();
    const [, setSidebarView] = useSidebarView();
    const [, setShowStartRecordingModal] = useStartRecordingModal();
    const { disableAudio } = useCallState();
    const orderedParticipantIds = useOrderedParticipantIds();
    const {
      broadcastRole,
      enableBreakoutRooms,
      enableChat,
      enableEmojiReactions,
      enableHandRaising,
      enablePeopleUI,
      enableRecording,
      enableRecordingUI,
    } = useCallConfig();
    const {
      breakoutHost,
      breakoutStatus,
      currentBreakoutRoom,
      isRequestingHelp,
      updateAskForHelpStatus,
    } = useBreakoutSessionState();
    const chatUnread = useChatUnread(currentBreakoutRoom.id);
    const { stopRecording } = useRecording();
    const { isLocalParticipantRecorded, isRecording } = useDailyRecording();
    const [recordingUIState] = useRecordingUI();
    const [showMenu, setShowMenu] = useShowMobileMoreMenu();
    const [snackbarShown, setSnackbarShown] = useState(false);
    const [menuLeft, setMenuLeft] = useState(0);
    const { waitingParticipants } = useWaitingParticipants();
    const [customTrayButtons] = useCustomTrayButtons();
    const { sendMessageToDriver } = useIframeDriver();
    const fitsRecordingButton =
      useMediaQuery('screen and (min-width: 350px)') &&
      !(enableEmojiReactions && enableHandRaising);
    const handRaiseBadgeStatus = useHandRaisingBadgeStatus();
    const localSessionId = useLocalSessionId();
    const isLocalOwner = useParticipantProperty(localSessionId, 'owner');
    const { canControlIntegration, mainIntegrations } = useCustomIntegrations();
    const apps = mainIntegrations.filter(canControlIntegration);
    const [, setShowAppsModal] = useAppsModal();

    const isAttendee = useMemo(
      () => broadcastRole === 'attendee',
      [broadcastRole]
    );

    const handleMenuShow = useCallback(() => {
      setShowMenu(true);
    }, [setShowMenu]);
    const handleMenuHide = () => {
      setShowMenu(false);
    };
    const handleAskForHelpClick = useCallback(() => {
      updateAskForHelpStatus(!isRequestingHelp);
      setShowMenu(false);
    }, [isRequestingHelp, setShowMenu, updateAskForHelpStatus]);
    const handlePeopleClick = useCallback(() => {
      setSidebarView('people');
      setShowMenu(false);
    }, [setShowMenu, setSidebarView]);
    const handleChatClick = useCallback(() => {
      setSidebarView('chat');
      setShowMenu(false);
    }, [setShowMenu, setSidebarView]);
    const handleRecordClick = useCallback(() => {
      if (isRecording) {
        stopRecording();
      } else {
        setShowStartRecordingModal(true);
      }
      setShowMenu(false);
    }, [isRecording, setShowMenu, setShowStartRecordingModal, stopRecording]);
    const handleSettingsClick = useCallback(() => {
      isAttendee ? setSettingsView('quality') : setSettingsView('devices');
      setShowMenu(false);
    }, [isAttendee, setSettingsView, setShowMenu]);
    const handleSettingsModalClose = () => {
      setSettingsView(null);
    };
    const handleBreakoutsClick = useCallback(() => {
      setSidebarView('breakout');
      setShowMenu(false);
    }, [setShowMenu, setSidebarView]);
    const handleAppsClick = useCallback(() => {
      setShowAppsModal(true);
      setShowMenu(false);
    }, [setShowAppsModal, setShowMenu]);
    const handleCustomButtonClick = useCallback(
      (id) => {
        sendMessageToDriver({
          action: ACTION_CUSTOM_BUTTON_CLICK,
          button_id: id,
        });
        setShowMenu(false);
      },
      [sendMessageToDriver, setShowMenu]
    );

    useEffect(() => {
      if (chatUnread && !snackbarShown) {
        addMessage({
          content: t('chat.started'),
          type: 'info',
          icon: <ChatIcon id="mobileChatSnackbarIcon" size={16} />,
        });
        setSnackbarShown(true);
      }
    }, [addMessage, chatUnread, snackbarShown, t]);

    useResize(
      useCallback(() => {
        if (!trayRef?.current || !showMenu) return;
        const tray = trayRef?.current?.getBoundingClientRect();
        setMenuLeft(tray.left);
      }, [showMenu])
    );

    const menuItems = useMemo(() => {
      const items: React.ComponentProps<typeof MobileMoreMenu>['items'] = [];
      if (enablePeopleUI) {
        items.push({
          label: (
            <Text variant="large" El="p">
              {t('general.people')}
            </Text>
          ),
          icon: (
            <div style={{ position: 'relative' }}>
              <UserIcon />
              <TrayBadge
                color={colors.system.red}
                visible={
                  waitingParticipants?.length > 0 || handRaiseBadgeStatus
                }
                top={0}
              />
            </div>
          ),
          id: 'mobile-more-menu-people',
          onClick: handlePeopleClick,
        });
      }
      // Show chat for any call chat is enabled in
      if (enableChat) {
        items.push({
          label: (
            <Text variant="large" El="p">
              {t('general.chat')}
            </Text>
          ),
          icon: (
            <div style={{ position: 'relative' }}>
              <ChatIcon />
              <TrayBadge
                color={colors.system.red}
                visible={chatUnread}
                top={0}
              />
            </div>
          ),
          id: 'mobile-more-menu-chat',
          onClick: handleChatClick,
        });
      }
      if (enableRecording && !fitsRecordingButton && enableRecordingUI) {
        const disabled =
          recordingUIState === 'saved' ||
          (isRecording && !isLocalParticipantRecorded) ||
          breakoutStatus === 'ongoing';
        items.push({
          disabled,
          label: (
            <Text color={disabled ? 'muted' : 'default'} variant="large" El="p">
              {isRecording
                ? t('recording.stopRecording')
                : t('recording.start')}
            </Text>
          ),
          icon: (
            <RecordIcon
              color={isRecording ? colors.system.white : undefined}
              style={
                isRecording
                  ? {
                      background: colors.system.red,
                      borderRadius: 4,
                    }
                  : {}
              }
            />
          ),
          id: `mobile-more-menu-${isRecording ? 'stop' : 'start'}-recording`,
          onClick: handleRecordClick,
        });
      }
      if (enableBreakoutRooms) {
        if (isLocalOwner) {
          items.push({
            label: (
              <Text variant="large" El="p">
                {t('general.breakoutRooms')}
              </Text>
            ),
            icon: <BreakoutIcon />,
            id: 'mobile-more-menu-breakouts',
            onClick: handleBreakoutsClick,
          });
        }
        if (breakoutStatus === 'ongoing') {
          items.push({
            disabled: orderedParticipantIds.includes(breakoutHost),
            label: (
              <Text
                color={
                  orderedParticipantIds.includes(breakoutHost)
                    ? 'muted'
                    : 'inherit'
                }
                variant="large"
                El="p"
              >
                {isRequestingHelp
                  ? t('breakouts.cancelRequestForHelp')
                  : t('breakouts.askHostForHelp')}
              </Text>
            ),
            icon: (
              <div
                style={{
                  borderRadius: 4,
                  background: isRequestingHelp ? colors.system.red : undefined,
                }}
              >
                <HelpIcon
                  color={isRequestingHelp ? colors.system.white : undefined}
                />
              </div>
            ),
            id: 'mobile-more-menu-ask-for-help',
            onClick: handleAskForHelpClick,
          });
        }
      }
      if (apps.length) {
        items.push({
          label: (
            <Text variant="large" El="p">
              {t('general.appsEllipsis')}
            </Text>
          ),
          icon: <AppsIcon />,
          onClick: handleAppsClick,
        });
      }
      items.push(
        ...Object.entries(customTrayButtons).map(([id, btn]) => ({
          label: (
            <Text variant="large" El="p">
              {btn.tooltip}
            </Text>
          ),
          icon: (
            <CustomIcon
              alt={btn.label}
              src={
                isDarkMode && btn.iconPathDarkMode
                  ? btn.iconPathDarkMode
                  : btn.iconPath
              }
              padding={4}
              size={24}
              style={{ display: 'block' }}
            />
          ),
          id: `mobile-more-menu-custom-${id}`,
          onClick: () => handleCustomButtonClick(id),
        }))
      );
      // Always show settings
      items.push({
        label: (
          <Text variant="large" El="p">
            {t('general.settings')}
          </Text>
        ),
        icon: <GearIcon fill="currentColor" stroke="transparent" />,
        id: 'mobile-more-menu-settings',
        onClick: handleSettingsClick,
      });
      return items;
    }, [
      apps.length,
      breakoutHost,
      breakoutStatus,
      chatUnread,
      colors.system.red,
      colors.system.white,
      customTrayButtons,
      enableBreakoutRooms,
      enableChat,
      enablePeopleUI,
      enableRecording,
      enableRecordingUI,
      fitsRecordingButton,
      handRaiseBadgeStatus,
      handleAppsClick,
      handleAskForHelpClick,
      handleBreakoutsClick,
      handleChatClick,
      handleCustomButtonClick,
      handlePeopleClick,
      handleRecordClick,
      handleSettingsClick,
      isDarkMode,
      isLocalOwner,
      isLocalParticipantRecorded,
      isRecording,
      isRequestingHelp,
      orderedParticipantIds,
      recordingUIState,
      t,
      waitingParticipants?.length,
    ]);

    const menuId = 'mobileTrayMoreMenu';
    const btnId = 'mobileTrayMoreButton';

    return (
      <div
        ref={trayRef}
        className={classnames('mobile-tray', {
          hidden: !showMobileTray,
        })}
      >
        <div className="left">
          {!isAttendee && <VideoControls id="mobile-video-controls" />}
          {!isAttendee && !disableAudio && (
            <AudioControls id="mobile-audio-controls" />
          )}
          {enableEmojiReactions && (
            <EmojiReactionControls id="mobile-emoji-reaction-controls" />
          )}
          {!isAttendee && enableHandRaising && (
            <RaiseHandControls id="mobile-raisehand-controls" />
          )}
          {enableRecording && fitsRecordingButton && enableRecordingUI && (
            <RecordControls id="mobile-record-controls" />
          )}
        </div>
        {isAttendee && (
          <div className="attendee-msg">
            <Text variant="strong">{t('broadcast.viewingCall')}</Text>
          </div>
        )}
        <TrayButton
          aria-controls={menuId}
          id={btnId}
          className={classnames(
            'btn',
            'mobile',
            robotsClassName('mobile-more-btn')
          )}
          disabled={!showMobileTray}
          onClick={handleMenuShow}
          style={{ padding: '4px 12px' }}
        >
          <EllipsisIcon />
          <Text>{t('general.more')}</Text>
          <TrayBadge
            color={colors.system.red}
            visible={
              chatUnread ||
              waitingParticipants.length > 0 ||
              handRaiseBadgeStatus
            }
          />
        </TrayButton>
        {showMenu && (
          <MobileMoreMenu
            aria-labelledby={btnId}
            id={menuId}
            items={menuItems}
            onClose={handleMenuHide}
            style={{
              left: menuLeft,
              width: trayRef.current?.clientWidth,
            }}
          />
        )}
        {settingsView && <SettingsModal onClose={handleSettingsModalClose} />}
        <style jsx>{`
          .mobile-tray {
            align-items: center;
            background-color: ${colors.backgroundAccent};
            border-radius: 8px;
            bottom: 56px;
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            left: 50%;
            max-width: min(calc(100% - 8px), ${TRAY_MAX_WIDTH}px);
            opacity: 1;
            padding: 7px 4px;
            position: absolute;
            transform: translateX(-50%);
            transition: opacity 0.2s ease, transform 0.2s ease;
            width: 100%;
            z-index: 10;
          }
          .mobile-tray.hidden {
            opacity: 0;
            transform: translate(-50%, 200%);
          }
          .mobile-tray .left {
            align: center;
            display: flex;
            flex-direction: row;
            margin-right: auto;
          }
          .mobile-tray.hidden button {
            pointer-events: none;
          }
          .mobile-tray div {
            position: relative;
          }
          .ellipsis {
            transform: rotate(90deg);
          }
          .attendee-msg {
            align-items: center;
            display: flex;
            height: ${CONTENT_HEIGHT}px;
            margin: 0 auto;
            padding: 0 8px;
          }
        `}</style>
      </div>
    );
  }
);
MobileTray.displayName = 'MobileTray';
